import { ErrorHandler, Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import { WINDOW } from '@libs/shared/utilities/tokens/window.token';

import { PAYMENT_COMPLETE_PATH } from '../../constants/payment-complete.constant';
import { PAYMENT_APP_ORIGIN } from '../../tokens/payment-app-origin.token';
import { IntegratedPaymentOutcome } from '../../types/integrated-payment-outcome.type';

export const SESSION_STORAGE_KEY = 'isEmbeddedInIframe-paymentId';

@Injectable({
  providedIn: 'root',
})
export class IntegratedPaymentsHelperService {
  constructor(
    @Inject(WINDOW) private window: Window,
    @Inject(ErrorHandler) private errorHandler: ErrorHandler,
    // Optional: only the integrated-payments app provides it. When absent (e.g.
    // the kodypayments app, which shares this service via the brand components),
    // the internal-completion path is simply never taken.
    @Optional() @Inject(PAYMENT_APP_ORIGIN) private paymentAppOrigin: string | null,
    private router: Router,
  ) {}

  getPaymentId() {
    return this.window.location.pathname.split('/')[1];
  }

  isIframe() {
    return this.window !== this.window.parent;
  }

  isEmbeddedInIframeFn() {
    try {
      const urlParams = new URLSearchParams(this.window.location.search);
      const isEmbeddedInIframe = !!urlParams.get('isEmbeddedInIframe');

      if (isEmbeddedInIframe && this.isIframe()) {
        return true;
      }

      // Save the paymentId to handle redirect checks
      if (this.getPaymentId() === this.window.sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        return true;
      }

      return false;
    } catch (e) {
      return false;
    }
  }

  redirectToReturnUrl(returnUrl: string | undefined, outcome: IntegratedPaymentOutcome): void {
    try {
      if (this.isEmbeddedInIframeFn()) {
        this.window.parent.postMessage({ type: 'PAYMENT_COMPLETE', outcome }, '*');
        return;
      }

      // OPI (and similar integrations) set the return_url to our own completion
      // page since they have no merchant site to return to. Keep the shopper in
      // the app and show our result page instead of doing a full external redirect.
      // Only the paymentId is passed; the page queries the real status itself, so
      // the (unreliable) outcome computed here is deliberately not forwarded.
      if (this.isInternalCompleteReturnUrl(returnUrl)) {
        this.router.navigate([PAYMENT_COMPLETE_PATH], {
          queryParams: { paymentId: this.getPaymentId() },
        });
        return;
      }

      if (!returnUrl) {
        throw new Error('Return url not defined');
      }
      // Redirect to the merchant's return_url as-is. We don't append ?status=
      // because merchants query the payment status via the API themselves, so
      // the param carried no reliable meaning.
      this.window.location.href = returnUrl;
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  private isInternalCompleteReturnUrl(returnUrl: string | undefined): boolean {
    // No canonical origin configured (e.g. kodypayments) => never treat as internal.
    if (!returnUrl || !this.paymentAppOrigin) {
      return false;
    }
    try {
      // Compare against our canonical origin (p-dev / p-staging / p.kody.com) rather
      // than window.location.origin so this also matches while serving from localhost
      // against a staging backend. Requiring the origin to match means we never
      // swallow a genuine external redirect that happens to share the path.
      const url = new URL(returnUrl, this.paymentAppOrigin);
      return url.origin === this.paymentAppOrigin && url.pathname.replace(/\/+$/, '') === PAYMENT_COMPLETE_PATH;
    } catch {
      return false;
    }
  }
}
